import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { fileUrl } from "../../lib/fileUrl";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, Lightbulb, MessageCircle, Plus, Send,
  Clock, CheckCircle2, Wrench, ChevronRight, Camera, Trash2,
  User, Lock, Eye, Download
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPI = [
  { key: "problema", label: "Problema", icon: AlertTriangle, desc: "Guasto o urgenza", badgeBg: "bg-red-100", badgeText: "text-red-700", iconBg: "bg-red-500" },
  { key: "proposta", label: "Proposta", icon: Lightbulb, desc: "Idea o suggerimento", badgeBg: "bg-amber-100", badgeText: "text-amber-700", iconBg: "bg-amber-500" },
  { key: "comunicazione", label: "Info", icon: MessageCircle, desc: "Comunicazione", badgeBg: "bg-blue-100", badgeText: "text-blue-700", iconBg: "bg-blue-500" },
];

const STATI = [
  { key: "aperta", label: "Aperta", icon: Clock, badgeBg: "bg-red-100", badgeText: "text-red-700" },
  { key: "in_gestione", label: "In gestione", icon: Wrench, badgeBg: "bg-amber-100", badgeText: "text-amber-700" },
  { key: "risolta", label: "Risolta", icon: CheckCircle2, badgeBg: "bg-green-100", badgeText: "text-green-700" },
];

const getTipo = (key) => TIPI.find(t => t.key === key) || TIPI[0];
const getStato = (key) => STATI.find(s => s.key === key) || STATI[0];
const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
};

export default function Segnalazioni() {
  const { localeCorrente, getAuthHeaders, user, isAdmin } = useAuth();
  const [segnalazioni, setSegnalazioni] = useState([]);
  const [conteggi, setConteggi] = useState({});
  const [filtroStato, setFiltroStato] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [filtroMie, setFiltroMie] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [nuovaRisposta, setNuovaRisposta] = useState("");
  const [operatori, setOperatori] = useState([]);

  const [newTipo, setNewTipo] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [newFoto, setNewFoto] = useState(null);
  const [newPrivata, setNewPrivata] = useState(false);
  const [sending, setSending] = useState(false);

  const headers = getAuthHeaders();
  const localeId = localeCorrente?.id;

  const fetchData = useCallback(async () => {
    if (!localeId) return;
    setLoading(true);
    try {
      let url = `${API}/api/segnalazioni-locale?locale_id=${localeId}`;
      if (filtroStato) url += `&stato=${filtroStato}`;
      if (filtroTipo) url += `&tipo=${filtroTipo}`;
      if (filtroMie) url += `&mie=true`;
      const [segRes, cntRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/api/segnalazioni-locale/conteggi?locale_id=${localeId}`, { headers }),
      ]);
      setSegnalazioni(segRes.data);
      setConteggi(cntRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [localeId, filtroStato, filtroTipo, filtroMie]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!localeId || !isAdmin) return;
    axios.get(`${API}/api/auth/users?societa_id=${user?.societa_id}`, { headers })
      .then(r => setOperatori(r.data || []))
      .catch(() => {});
  }, [localeId, isAdmin]);

  const handleCreate = async () => {
    if (!newTipo || !newMsg.trim()) { toast.error("Seleziona tipo e scrivi un messaggio"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/segnalazioni-locale?locale_id=${localeId}`, {
        tipo: newTipo, messaggio: newMsg.trim(), foto_base64: newFoto, privata: newPrivata
      }, { headers });
      toast.success("Segnalazione inviata!");
      setShowNew(false); setNewTipo(null); setNewMsg(""); setNewFoto(null); setNewPrivata(false);
      fetchData();
    } catch (e) { toast.error("Errore nell'invio"); }
    setSending(false);
  };

  const handleStato = async (id, stato, assegnato_a_id = null) => {
    try {
      const res = await axios.put(`${API}/api/segnalazioni-locale/${id}/stato`, { stato, assegnato_a_id }, { headers });
      toast.success(`Stato aggiornato: ${getStato(stato).label}`);
      setDettaglio(res.data);
      fetchData();
    } catch (e) { toast.error("Errore aggiornamento"); }
  };

  const handleRisposta = async () => {
    if (!nuovaRisposta.trim() || !dettaglio) return;
    try {
      const res = await axios.post(`${API}/api/segnalazioni-locale/${dettaglio.id}/risposte`, { messaggio: nuovaRisposta.trim() }, { headers });
      setDettaglio(res.data);
      setNuovaRisposta("");
      fetchData();
    } catch (e) { toast.error("Errore invio risposta"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/segnalazioni-locale/${id}`, { headers });
      toast.success("Segnalazione eliminata");
      setDettaglio(null);
      fetchData();
    } catch (e) { toast.error("Errore eliminazione"); }
  };

  const handleNascondi = async (id) => {
    try {
      await axios.put(`${API}/api/segnalazioni-locale/${id}/nascondi`, {}, { headers });
      toast.success("Segnalazione nascosta");
      setDettaglio(null);
      fetchData();
    } catch (e) { toast.error("Errore"); }
  };

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setNewFoto(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-5" data-testid="segnalazioni-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground" data-testid="segnalazioni-title">Segnalazioni</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Problemi, proposte e comunicazioni dal team</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-primary hover:bg-primary/90 text-primary-foreground w-full sm:w-auto" data-testid="nuova-segnalazione-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuova segnalazione
        </Button>
      </div>

      {/* Conteggi */}
      <div className="grid grid-cols-3 gap-3">
        {STATI.map(s => {
          const count = conteggi[s.key] || 0;
          const isActive = filtroStato === s.key;
          return (
            <button key={s.key} onClick={() => setFiltroStato(isActive ? null : s.key)}
              className={`p-3 bg-card rounded-2xl border transition-all text-center ${isActive ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/30"}`}
              data-testid={`filtro-stato-${s.key}`}>
              <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.badgeText}`} />
              <p className={`text-xl font-bold ${s.badgeText}`}>{count}</p>
              <p className="text-[11px] font-medium text-muted-foreground">{s.label}</p>
            </button>
          );
        })}
      </div>

      {/* Filtri tipo + Le mie */}
      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => setFiltroMie(!filtroMie)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-all ${filtroMie ? "bg-indigo-600 text-white border-transparent" : "bg-indigo-50 text-indigo-700 border-transparent"}`}
          data-testid="filtro-mie-btn">
          <User className="h-3.5 w-3.5" /> Le mie
        </button>
        <div className="w-px h-5 bg-border" />
        {TIPI.map(t => {
          const isActive = filtroTipo === t.key;
          return (
            <button key={t.key} onClick={() => setFiltroTipo(isActive ? null : t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border transition-all ${isActive ? `${t.iconBg} text-white border-transparent` : `${t.badgeBg} ${t.badgeText} border-transparent`}`}
              data-testid={`filtro-tipo-${t.key}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Caricamento...</div>
      ) : segnalazioni.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-2xl border border-border">
          <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-foreground font-medium">Nessuna segnalazione</p>
          <p className="text-sm text-muted-foreground mt-1">Le segnalazioni appariranno qui</p>
        </div>
      ) : (
        <div className="space-y-3">
          {segnalazioni.map(seg => {
            const tipo = getTipo(seg.tipo);
            const stato = getStato(seg.stato);
            const Icon = tipo.icon;
            return (
              <button key={seg.id} onClick={() => setDettaglio(seg)}
                className="w-full text-left p-4 bg-card rounded-2xl border border-border hover:border-primary/30 transition-colors shadow-sm"
                data-testid={`segnalazione-item-${seg.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl ${tipo.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-bold uppercase tracking-wider ${tipo.badgeText}`}>{tipo.label}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${stato.badgeBg} ${stato.badgeText}`}>{stato.label}</span>
                      {seg.privata && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </div>
                    <p className="text-sm text-foreground line-clamp-2 leading-relaxed">{seg.messaggio}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-xs text-muted-foreground font-medium">{seg.creato_da?.nome}</span>
                      <span className="text-xs text-muted-foreground/60">{formatDate(seg.created_at)}</span>
                      {seg.risposte?.length > 0 && (
                        <span className="text-xs text-primary font-medium">{seg.risposte.length} rispost{seg.risposte.length === 1 ? "a" : "e"}</span>
                      )}
                      {seg.foto_url && <Camera className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-3" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog Nuova Segnalazione */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nuova segnalazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            {/* Tipo selection - bottoni grandi e leggibili */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">Tipo di segnalazione</p>
              <div className="space-y-2">
                {TIPI.map(t => {
                  const Icon = t.icon;
                  const isSelected = newTipo === t.key;
                  return (
                    <button key={t.key} onClick={() => setNewTipo(t.key)}
                      className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border bg-card hover:border-primary/30"}`}
                      data-testid={`tipo-btn-${t.key}`}>
                      <div className={`w-12 h-12 rounded-xl ${t.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold ${isSelected ? "text-primary" : "text-foreground"}`}>{t.label}</p>
                        <p className="text-sm text-muted-foreground">{t.desc}</p>
                      </div>
                      {isSelected && <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Messaggio */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Descrizione</p>
              <Textarea
                placeholder="Descrivi la segnalazione..."
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                rows={4}
                className="resize-none text-foreground"
                data-testid="segnalazione-messaggio"
              />
            </div>

            {/* Foto - pulsante grande e chiaro */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Foto (opzionale)</p>
              {newFoto ? (
                <div className="relative">
                  <img src={newFoto} alt="Anteprima" className="w-full h-48 object-cover rounded-2xl border border-border" />
                  <button onClick={() => setNewFoto(null)}
                    className="absolute top-2 right-2 bg-card/90 backdrop-blur-sm rounded-full p-1.5 border border-border shadow-sm">
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-3 p-5 rounded-2xl border-2 border-dashed border-border cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors"
                  data-testid="segnalazione-foto-label">
                  <Camera className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">Scatta o allega una foto</span>
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFoto} data-testid="segnalazione-foto" />
                </label>
              )}
            </div>

            {/* Privata toggle */}
            <button
              onClick={() => setNewPrivata(!newPrivata)}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-all text-left ${newPrivata ? "border-primary bg-primary/5" : "border-border bg-card"}`}
              data-testid="segnalazione-privata-toggle"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${newPrivata ? "bg-primary" : "bg-muted"}`}>
                {newPrivata ? <Lock className="h-5 w-5 text-white" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
              </div>
              <div className="flex-1">
                <p className={`font-medium ${newPrivata ? "text-primary" : "text-foreground"}`}>Segnalazione privata</p>
                <p className="text-xs text-muted-foreground">Visibile solo agli admin</p>
              </div>
              <div className={`w-11 h-6 rounded-full transition-colors flex items-center px-0.5 ${newPrivata ? "bg-primary" : "bg-muted"}`}>
                <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${newPrivata ? "translate-x-5" : "translate-x-0"}`} />
              </div>
            </button>

            <Button onClick={handleCreate} disabled={sending || !newTipo || !newMsg.trim()} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base" data-testid="invia-segnalazione-btn">
              {sending ? "Invio..." : "Invia segnalazione"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettaglio */}
      <Dialog open={!!dettaglio} onOpenChange={() => setDettaglio(null)}>
        <DialogContent className="max-w-lg mx-auto max-h-[90vh] overflow-y-auto">
          {dettaglio && (() => {
            const tipo = getTipo(dettaglio.tipo);
            const stato = getStato(dettaglio.stato);
            const TipoIcon = tipo.icon;
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl ${tipo.iconBg} flex items-center justify-center`}>
                      <TipoIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <DialogTitle className="text-foreground">{tipo.label}</DialogTitle>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${stato.badgeBg} ${stato.badgeText}`}>{stato.label}</span>
                    {dettaglio.privata && (
                      <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-medium flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Privata
                      </span>
                    )}
                  </div>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  {/* Messaggio */}
                  <div className="p-4 bg-muted/50 rounded-2xl">
                    <p className="text-sm text-foreground leading-relaxed">{dettaglio.messaggio}</p>
                    {dettaglio.foto_url && (
                      <div className="mt-3 relative group">
                        <img src={fileUrl(dettaglio.foto_url)} alt="Foto" className="rounded-xl w-full max-h-64 object-cover border border-border" />
                        <a
                          href={fileUrl(dettaglio.foto_url)}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          data-testid="download-segnalazione-foto"
                          title="Scarica foto"
                        >
                          <Download className="h-4 w-4 text-white" />
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{dettaglio.creato_da?.nome} - {formatDate(dettaglio.created_at)}</span>
                    </div>
                  </div>

                  {dettaglio.assegnato_a && (
                    <div className="text-sm text-foreground px-1">Assegnato a: <b>{dettaglio.assegnato_a.nome}</b></div>
                  )}

                  {/* Stato buttons (admin only) */}
                  {isAdmin && (
                    <div className="grid grid-cols-3 gap-2">
                      {STATI.map(s => {
                        const SIcon = s.icon;
                        const isCurrentStato = dettaglio.stato === s.key;
                        return (
                          <Button key={s.key} variant={isCurrentStato ? "default" : "outline"} size="sm"
                            onClick={() => handleStato(dettaglio.id, s.key)}
                            className={isCurrentStato ? "bg-primary text-primary-foreground" : "text-foreground border-border"}
                            data-testid={`stato-btn-${s.key}`}>
                            <SIcon className="h-3.5 w-3.5 mr-1" /> {s.label}
                          </Button>
                        );
                      })}
                    </div>
                  )}

                  {/* Assign (admin) */}
                  {isAdmin && operatori.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1.5">Assegna a:</p>
                      <select
                        className="w-full border border-border rounded-xl px-3 py-2.5 text-sm text-foreground bg-card"
                        value={dettaglio.assegnato_a?.id || ""}
                        onChange={e => handleStato(dettaglio.id, dettaglio.stato, e.target.value || null)}
                        data-testid="assegna-select"
                      >
                        <option value="">Nessuno</option>
                        {operatori.map(op => (
                          <option key={op.id} value={op.id}>{op.nome || op.email || op.telefono}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Risposte */}
                  {dettaglio.risposte?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-foreground">Risposte</p>
                      {dettaglio.risposte.map(r => (
                        <div key={r.id} className="p-3 bg-primary/5 rounded-xl border border-primary/10">
                          <p className="text-sm text-foreground">{r.messaggio}</p>
                          <p className="text-xs text-muted-foreground mt-1.5">{r.autore?.nome} - {formatDate(r.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Nuova risposta */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Scrivi una risposta..."
                      value={nuovaRisposta}
                      onChange={e => setNuovaRisposta(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleRisposta()}
                      className="flex-1 text-foreground"
                      data-testid="nuova-risposta-input"
                    />
                    <Button onClick={handleRisposta} disabled={!nuovaRisposta.trim()} size="icon" className="bg-primary text-primary-foreground h-11 w-11" data-testid="invia-risposta-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Delete (admin) */}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 w-full"
                      onClick={() => handleDelete(dettaglio.id)} data-testid="elimina-segnalazione-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina segnalazione
                    </Button>
                  )}

                  {/* Nascondi (operatore, segnalazioni risolte) */}
                  {dettaglio.stato === "risolta" && dettaglio.creato_da?.id === user?.id && (
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-muted w-full"
                      onClick={() => handleNascondi(dettaglio.id)} data-testid="nascondi-segnalazione-btn">
                      <Eye className="h-3.5 w-3.5 mr-1" /> Ho visionato, nascondi
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
